import { useEffect, useMemo, useState } from 'react';
import { cargoService, usuarioService } from '@/services/api';
import { Cargo, Usuario } from '@/types';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

/** Radix Select não aceita value="" em item — "Nenhum" usa este valor e vira `null` na saída. */
const NENHUM = 'nenhum';

/**
 * Busca de opções por empresa compartilhada só enquanto está EM ANDAMENTO: instâncias montadas juntas
 * (mesma empresa) fazem uma requisição de cargos e uma de usuários. Terminada, sai do mapa — a próxima
 * montagem busca de novo, então um cargo ou usuário recém-criado sempre aparece.
 */
type Opcoes = { ok: boolean; cargos: Cargo[]; usuarios: Usuario[] };
const buscasEmAndamento = new Map<string, Promise<Opcoes>>();

function carregarOpcoes(empresaId: string): Promise<Opcoes> {
  const emAndamento = buscasEmAndamento.get(empresaId);
  if (emAndamento) return emAndamento;
  const promessa = Promise.all([
    cargoService.getAll({ empresa_id: empresaId }),
    usuarioService.getByEmpresa(empresaId),
  ])
    .then(([c, u]) => ({
      ok: c.success && u.success,
      cargos: c.success ? c.data : [],
      usuarios: u.success ? u.data : [],
    }))
    .catch(() => ({ ok: false, cargos: [], usuarios: [] }))
    .finally(() => buscasEmAndamento.delete(empresaId));
  buscasEmAndamento.set(empresaId, promessa);
  return promessa;
}

interface Props {
  /** Empresa do vínculo — cargos e gestores são sempre dela. */
  empresaId: string;
  cargoId: string | null;
  gestorId: string | null;
  onChange: (campo: 'cargo_id' | 'gestor_usuario_id', valor: string | null) => void;
  /** Pessoa em edição, para ela não aparecer como gestor de si mesma. */
  usuarioId?: string;
  /** Nome do gestor atual, para exibi-lo mesmo que já tenha sido desativado (a lista só traz ativos). */
  gestorNome?: string | null;
  /** Versão enxuta (rótulos acima dos campos menores) para listas de vínculos. */
  compacto?: boolean;
}

/** Selects "Cargo" e "Gestor" de um vínculo usuário–empresa (ambos opcionais). */
export function CargoGestorSelects({ empresaId, cargoId, gestorId, onChange, usuarioId, gestorNome, compacto = false }: Props) {
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregado, setCarregado] = useState(false);
  const [erroCarga, setErroCarga] = useState(false);

  useEffect(() => {
    if (!empresaId) {
      setCargos([]);
      setUsuarios([]);
      return;
    }
    let cancelado = false;
    setCarregado(false);
    setErroCarga(false);
    carregarOpcoes(empresaId).then((opcoes) => {
      if (cancelado) return;
      setCargos(opcoes.cargos);
      setUsuarios(opcoes.usuarios);
      // Com falha, a lista não é confiável: não marca o gestor atual como "desativado" só por faltar nela.
      setCarregado(opcoes.ok);
      setErroCarga(!opcoes.ok);
    });
    return () => {
      cancelado = true;
    };
  }, [empresaId]);

  const gestores = useMemo(
    () =>
      usuarios
        .filter((u) => u.ativo !== false && u.tipo !== 'superadmin' && u.id !== usuarioId)
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [usuarios, usuarioId],
  );
  // Gestor atual fora da lista continua visível até alguém trocar. Com a lista carregada, é porque saiu
  // (desativado); se a carga falhou, só não dá para saber — mostra o nome sem marcar.
  const gestorAtualForaDaLista =
    gestorId && (carregado || erroCarga) && !gestores.some((u) => u.id === gestorId)
      ? {
          id: gestorId,
          rotulo: carregado
            ? (gestorNome ? `${gestorNome} (desativado)` : 'Gestor desativado')
            : (gestorNome || 'Gestor atual'),
        }
      : null;

  const triggerClass = compacto ? 'h-7 text-xs' : undefined;
  const idCargo = `cargo_${usuarioId ?? 'novo'}_${empresaId}`;
  const idGestor = `gestor_${usuarioId ?? 'novo'}_${empresaId}`;

  return (
    <div className={cn('grid gap-3', compacto ? 'grid-cols-2' : 'grid-cols-1')}>
      <div className="space-y-1.5">
        <Label htmlFor={idCargo} className={compacto ? 'text-xs text-muted-foreground' : undefined}>Cargo</Label>
        <Select
          value={cargoId ?? NENHUM}
          onValueChange={(v) => onChange('cargo_id', v === NENHUM ? null : v)}
        >
          <SelectTrigger id={idCargo} className={triggerClass}>
            <SelectValue placeholder="Nenhum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NENHUM}>Nenhum</SelectItem>
            {cargos.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={idGestor} className={compacto ? 'text-xs text-muted-foreground' : undefined}>Gestor</Label>
        <Select
          value={gestorId ?? NENHUM}
          onValueChange={(v) => onChange('gestor_usuario_id', v === NENHUM ? null : v)}
        >
          <SelectTrigger id={idGestor} className={triggerClass}>
            <SelectValue placeholder="Nenhum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NENHUM}>Nenhum</SelectItem>
            {gestorAtualForaDaLista && (
              <SelectItem value={gestorAtualForaDaLista.id} disabled>
                {gestorAtualForaDaLista.rotulo}
              </SelectItem>
            )}
            {gestores.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {erroCarga && (
        <p className={cn('text-xs text-destructive', compacto && 'col-span-2')}>
          Não foi possível carregar cargos e gestores. Reabra para tentar de novo.
        </p>
      )}
    </div>
  );
}
